import React, { useState } from 'react';
import { isBlank } from 'utils/helperMethods';
import { find, propEq, isEmpty, filter, length, equals, take, pathOr, has } from 'ramda';
import PropTypes from 'prop-types';
import { Box, Tabs, Tab, Badge, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import ChatPresenter from 'presenters/ChatPresenter';
import ChatsPresenter from 'presenters/ChatsPresenter';
import MessagePresenter from 'presenters/MessagePresenter';
import FirebaseChatPresenter from 'presenters/FirebaseChatPresenter';
import BidRequestPresenter from 'presenters/BidRequestPresenter';

import Loader from 'sharedComponents/Loader';
import InfiniteScroll from 'react-infinite-scroll-component';
import BuyerCard from 'sharedComponents/BuyerCard';
import Icon from 'sharedComponents/Icon';

import useFirebaseChat from 'reducers/hooks/firebase/useChat';
import useStyles from './useStyles';

const INITIAL_CHATS_QUANTITY = 20;

const BuyersChatList = props => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { usersOnlineStatusList } = useFirebaseChat();
  const {
    selectedChatUid,
    onChatSelect,
    chats,
    bidRequests,
    onAddChatToFavorite,
    onRemoveChatFromFavorite,
    onAddChatToHidden,
    onRemoveChatFromHidden,
    onToggleAutoFollowupsMessages,
    isChatUpdating,
    chatsInfo,
    isEnabledAutoFollowupsMessages,
  } = props;

  const isNotHidden = chat => !has('isHidden')(chat) || propEq('isHidden', false)(chat);

  const allChats = filter(isNotHidden, chats);
  const hiddenChats = filter(propEq('isHidden', true), chats);
  const favoriteChats = filter(chat => isNotHidden(chat) && propEq('isFavorite', true)(chat), chats);
  const chatsWithTasks = filter(isNotHidden, ChatsPresenter.withTasks(chats));

  const allChatsCount = length(allChats);
  const hiddenChatsCount = length(hiddenChats);
  const favoriteChatsCount = length(favoriteChats);
  const chatsWithTasksCount = length(chatsWithTasks);

  const chatTabsMapper = {
    0: allChats,
    1: chatsWithTasks,
    2: favoriteChats,
    3: hiddenChats,
  };

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const selectedChats = chatTabsMapper[activeTabIndex];

  const [chatsQuantityToShow, setChatsQuantityToShow] = useState(INITIAL_CHATS_QUANTITY);
  const [hasMoreChats, setHasMoreChats] = useState(length(selectedChats) > chatsQuantityToShow);

  const handleTabChange = (_event, newValue) => {
    if (equals(newValue, activeTabIndex)) return;
    setActiveTabIndex(newValue);
    const selectedTabChats = chatTabsMapper[newValue];
    setHasMoreChats(length(selectedTabChats) > INITIAL_CHATS_QUANTITY);
    setChatsQuantityToShow(INITIAL_CHATS_QUANTITY);
  };

  const chatsToShow = take(chatsQuantityToShow, selectedChats);

  const loadMore = () => {
    const newChatsQuantity = chatsQuantityToShow + INITIAL_CHATS_QUANTITY;
    setChatsQuantityToShow(newChatsQuantity);
    setHasMoreChats(length(selectedChats) > newChatsQuantity);
  };

  return (
    <div className={classes.container}>
      <Tabs
        value={activeTabIndex}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
      >
        <Tab
          value={0}
          label={t('components.BuyersChatList.all')}
          icon={
            <Badge color="primary" badgeContent={allChatsCount} classes={{ badge: classes.badge }}>
              <Icon name="conversation" className={classes.tabIcon} />
            </Badge>
          }
          className={classes.tab}
        />
        <Tab
          label={t('components.BuyersChatList.tasks')}
          value={1}
          className={classes.tab}
          icon={
            <Badge color="primary" badgeContent={chatsWithTasksCount} classes={{ badge: classes.badge }}>
              <Icon name="task" className={classes.tabIcon} />
            </Badge>
          }
        />
        <Tab
          label={t('components.BuyersChatList.favorites')}
          value={2}
          className={classes.tab}
          icon={
            <Badge color="primary" badgeContent={favoriteChatsCount} classes={{ badge: classes.badge }}>
              <Icon name="contourStar" className={classes.tabIcon} />
            </Badge>
          }
        />
        <Tab
          label={t('components.BuyersChatList.hidden')}
          value={3}
          className={classes.tab}
          icon={
            <Badge color="primary" badgeContent={hiddenChatsCount} classes={{ badge: classes.badge }}>
              <Icon name="visibleOff" className={classes.tabIcon} />
            </Badge>
          }
        />
      </Tabs>
      <div className={classes.chatListContainer} id="scrollableDiv" key={activeTabIndex}>
        <InfiniteScroll
          dataLength={length(chatsToShow)}
          next={loadMore}
          hasMore={hasMoreChats}
          loader={<Loader />}
          endMessage={
            <Box textAlign="center" mt={2} height="100%" px={2}>
              <Typography className={classes.chatsEnd} variant="subtitle1">
                {t('components.BuyersChatList.chatsEnd')}
              </Typography>
            </Box>
          }
          scrollableTarget="scrollableDiv"
        >
          {chatsToShow.map(chat => {
            const chatUid = FirebaseChatPresenter.uid(chat);
            const buyerId = FirebaseChatPresenter.buyerId(chat);
            const chatInfo = chatsInfo[chatUid];
            const buyer = isBlank(chatInfo) ? null : ChatPresenter.buyer(chatInfo);
            const lastMessage = FirebaseChatPresenter.latestMessage(chat);
            const lastEventAt = MessagePresenter.formatedSentTimeFromNow(lastMessage);
            const unreadChatCount = FirebaseChatPresenter.unreadCount(chat);
            const isSelected = selectedChatUid === chatUid;
            const isOnline = pathOr(false, [buyerId, 'isOnline'], usersOnlineStatusList);
            const bidRequest = find(propEq('buyerId', buyerId), bidRequests);
            const isFavorite = FirebaseChatPresenter.isFavorite(chat);
            const isHidden = FirebaseChatPresenter.isHidden(chat);

            return (
              <BuyerCard
                key={chatUid}
                isOnline={isOnline}
                isHightlighted={isSelected}
                chatInfo={chatInfo}
                lastEventAt={lastEventAt}
                unreadCount={unreadChatCount}
                onClick={onChatSelect(chatUid)}
                buyer={buyer}
                bidRequest={bidRequest}
                onAddToFavorite={onAddChatToFavorite(chatUid)}
                onRemoveFromFavorite={onRemoveChatFromFavorite(chatUid)}
                onAddToHidden={onAddChatToHidden(chatUid)}
                onRemoveFromHidden={onRemoveChatFromHidden(chatUid)}
                onToggleAutoFollowupsMessages={onToggleAutoFollowupsMessages}
                isChatUpdating={isChatUpdating}
                isFavorite={isFavorite}
                isHidden={isHidden}
                isEnabledAutoFollowupsMessages={isEnabledAutoFollowupsMessages}
              />
            );
          })}
          {isEmpty(selectedChats) && (
            <Box width="100%" textAlign="center" mt={2} height="100%" px={2}>
              <Typography className={classes.chatsPlaceholder} variant="subtitle1">
                {t('components.BuyersChatList.chatsPlaceholder')}
              </Typography>
            </Box>
          )}
        </InfiniteScroll>
      </div>
    </div>
  );
};

BuyersChatList.propTypes = {
  chats: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  chatsInfo: PropTypes.shape().isRequired,
  bidRequests: PropTypes.arrayOf(BidRequestPresenter.shape()),
  selectedChatUid: PropTypes.string,
  onChatSelect: PropTypes.func.isRequired,
  isEnabledAutoFollowupsMessages: PropTypes.bool.isRequired,
  onAddChatToFavorite: PropTypes.func.isRequired,
  onRemoveChatFromFavorite: PropTypes.func.isRequired,
  onAddChatToHidden: PropTypes.func.isRequired,
  onRemoveChatFromHidden: PropTypes.func.isRequired,
};

BuyersChatList.defaultProps = {
  selectedChatUid: '',
  bidRequests: [],
};

export default BuyersChatList;



// WEBPACK FOOTER //
// ./src/sharedComponents/BuyersChatList/BuyersChatList.js